/**
 * Round-trip leg allocation.
 *
 * A round trip is charged once but stored as TWO `bookings` rows (outbound +
 * return), so every combined price has to be split into per-leg figures whose
 * sum is exactly what Stripe captured. `calculate-price` produces that split
 * (`legAllocation`) when it doubles a one-way price; this module covers the two
 * cases that split can't:
 *
 *  1. **It was never carried.** `legAllocation` lives only in memory — it is not
 *     a column on `booking_quotes`, so a quote resumed from its link (the GHL
 *     discount workflow always does this) arrives at checkout without one.
 *     `deriveLegAllocation` reconstructs it from the combined pricing instead of
 *     re-pricing the trip, so the customer is never quietly charged a number
 *     different from the one on the page they resumed.
 *
 *  2. **A discount landed after the split.** A quote discount applies to the
 *     combined grand total; `splitDiscountAcrossLegs` pushes it down into both
 *     legs so `outbound.total + return.total` still equals the charge. Without
 *     it the two rows record the undiscounted price while Stripe captured less,
 *     and the webhook marks both legs "paid in full" for money that never moved.
 *
 * Everything here is pure and immutable.
 */

import type { PricingLineItem } from './types'

const roundCents = (n: number): number => Math.round(n * 100) / 100

/** One leg of a round trip. Mirrors what `calculate-price` emits per leg. */
export interface LegPricing {
  subtotal: number
  gratuityAmount: number
  taxes: number
  total: number
  breakdown: PricingLineItem[]
}

/** The pair of legs a round-trip charge is split across. */
export interface LegAllocation {
  outbound: LegPricing
  return: LegPricing
}

/** The combined round-trip figures a split is derived from. */
export interface CombinedRoundTripPricing {
  subtotal: number
  gratuityAmount: number
  taxes: number
  total: number
  breakdown: PricingLineItem[]
}

/**
 * Whether a line is the automatic return-trip discount (`Return Trip Discount
 * (15%)`), which `applyRoundTripPricing` adds to the combined breakdown and to
 * the return leg only.
 *
 * It shares `type: 'discount'` with operator/quote discounts, so the label is
 * what separates them — same reasoning as `isOperatorDiscountLine`, and the two
 * predicates are deliberately mutually exclusive.
 */
export function isReturnTripDiscountLine(
  line: { label?: unknown; type?: unknown } | null | undefined
): boolean {
  if (!line || typeof line !== 'object' || line.type !== 'discount') return false
  return /^Return Trip Discount/.test(String(line.label ?? ''))
}

/**
 * Split a dollar amount across weights, pro-rata, without losing a cent.
 *
 * Each share is rounded down to the cent and the remainder goes to the largest
 * weight, so `sum(shares) === amount` exactly — the invariant the two booking
 * rows depend on. Zero/negative total weight splits evenly.
 */
export function allocateAmountByShare(amount: number, weights: number[]): number[] {
  if (weights.length === 0) return []
  const target = roundCents(amount)
  const safeWeights = weights.map((w) => (Number.isFinite(w) && w > 0 ? w : 0))
  const totalWeight = safeWeights.reduce((sum, w) => sum + w, 0)

  const shares =
    totalWeight > 0
      ? safeWeights.map((w) => Math.floor((target * w) / totalWeight * 100) / 100)
      : safeWeights.map(() => Math.floor((target / safeWeights.length) * 100) / 100)

  const distributed = roundCents(shares.reduce((sum, s) => sum + s, 0))
  const remainder = roundCents(target - distributed)
  if (remainder !== 0) {
    // The heaviest leg absorbs the leftover cent(s).
    const heaviest = safeWeights.reduce(
      (best, w, i) => (w > safeWeights[best] ? i : best),
      0
    )
    shares[heaviest] = roundCents(shares[heaviest] + remainder)
  }

  return shares
}

/** What `combineRoundTripLegs` returns: combined figures plus the per-leg split. */
export interface CombinedRoundTripLegs {
  subtotal: number
  gratuityAmount: number
  taxes: number
  total: number
  breakdown: PricingLineItem[]
  legAllocation: LegAllocation
  roundTripDiscount?: { percent: number; amount: number }
}

/**
 * Combine two INDEPENDENTLY priced legs into one round-trip price.
 *
 * The historical round-trip model doubles the outbound leg (`applyRoundTripPricing`
 * in calculate-price), which is only correct when the return leg mirrors the
 * outbound. When the customer sets different return addresses the return leg is
 * priced on its own — different distance, its own flat-rate match, its own fees —
 * and this function does the join:
 *
 *   outbound.total = A            (as priced)
 *   return.total   = B - D        (as priced, minus the return-trip discount)
 *   combined.total = A + B - D    (what the customer is charged)
 *
 * The discount keeps its existing semantic: a percentage of the RETURN leg's own
 * price, carried as a negative `Return Trip Discount (X%)` line on the return
 * breakdown — the exact label `isReturnTripDiscountLine` matches, so every
 * downstream consumer treats it identically to the doubled path.
 *
 * The combined breakdown merges lines with the same label+type (Base Fare +
 * Base Fare becomes one summed line, like the doubled path produced), and keeps
 * lines unique to one leg — e.g. a deadhead fee whose label carries that leg's
 * mileage — as their own entries.
 */
export function combineRoundTripLegs(
  outbound: LegPricing,
  returnLeg: LegPricing,
  discountPercent: number
): CombinedRoundTripLegs {
  const discountAmount =
    discountPercent > 0 ? roundCents(returnLeg.total * (discountPercent / 100)) : 0

  const finalReturnBreakdown: PricingLineItem[] = [
    ...returnLeg.breakdown.map((line) => ({ ...line })),
    ...(discountAmount > 0
      ? [
          {
            label: `Return Trip Discount (${discountPercent}%)`,
            amount: -discountAmount,
            type: 'discount' as const,
          },
        ]
      : []),
  ]

  const finalReturnLeg: LegPricing = {
    subtotal: roundCents(returnLeg.subtotal),
    gratuityAmount: roundCents(returnLeg.gratuityAmount),
    taxes: roundCents(returnLeg.taxes),
    total: roundCents(returnLeg.total - discountAmount),
    breakdown: finalReturnBreakdown,
  }

  // Merge by label+type, preserving outbound order and appending return-only
  // lines (including the discount, which never matches an outbound line).
  const merged = new Map<string, PricingLineItem>()
  // NUL as the separator so `type + label` can never collide with another
  // pair's concatenation; escaped so the source file stays plain text.
  const keyOf = (line: PricingLineItem) => `${line.type}\u0000${line.label}`
  for (const line of outbound.breakdown) {
    const key = keyOf(line)
    const existing = merged.get(key)
    merged.set(
      key,
      existing
        ? { ...existing, amount: roundCents(existing.amount + line.amount) }
        : { ...line }
    )
  }
  for (const line of finalReturnBreakdown) {
    const key = keyOf(line)
    const existing = merged.get(key)
    merged.set(
      key,
      existing
        ? { ...existing, amount: roundCents(existing.amount + line.amount) }
        : { ...line }
    )
  }

  return {
    subtotal: roundCents(outbound.subtotal + returnLeg.subtotal),
    gratuityAmount: roundCents(outbound.gratuityAmount + returnLeg.gratuityAmount),
    taxes: roundCents(outbound.taxes + returnLeg.taxes),
    total: roundCents(outbound.total + finalReturnLeg.total),
    breakdown: [...merged.values()],
    legAllocation: {
      outbound: {
        subtotal: roundCents(outbound.subtotal),
        gratuityAmount: roundCents(outbound.gratuityAmount),
        taxes: roundCents(outbound.taxes),
        total: roundCents(outbound.total),
        breakdown: outbound.breakdown.map((line) => ({ ...line })),
      },
      return: finalReturnLeg,
    },
    ...(discountAmount > 0
      ? { roundTripDiscount: { percent: discountPercent, amount: discountAmount } }
      : {}),
  }
}

/**
 * Reconstruct the per-leg split from a combined round-trip price.
 *
 * This is the inverse of `applyRoundTripPricing`: the combined breakdown is the
 * one-way breakdown doubled, plus (optionally) one `Return Trip Discount` line.
 * So halving every other line recovers the one-way leg, and:
 *
 *   D                = the return-trip discount already in the combined total
 *   outbound.total   = (combined.total + D) / 2   // one-way price P
 *   return.total     = combined.total - P         // P - D
 *
 * The legs are made to sum to the combined figures exactly rather than being
 * computed independently, so cent-level rounding can never leave the pair
 * disagreeing with what the customer is charged.
 *
 * Returns `null` when there is nothing sane to split (no positive total), which
 * the caller should treat as "cannot pair this booking".
 */
export function deriveLegAllocation(
  pricing: CombinedRoundTripPricing | null | undefined
): LegAllocation | null {
  if (!pricing || !Number.isFinite(pricing.total) || pricing.total <= 0) return null

  const breakdown = Array.isArray(pricing.breakdown) ? pricing.breakdown : []
  const returnDiscountLines = breakdown.filter(isReturnTripDiscountLine)

  // Stored as negative amounts; `discountAmount` is the positive magnitude.
  const discountAmount = roundCents(
    -returnDiscountLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0)
  )

  const oneWayBreakdown: PricingLineItem[] = breakdown
    .filter((line) => !isReturnTripDiscountLine(line))
    .map((line) => ({ ...line, amount: roundCents((Number(line.amount) || 0) / 2) }))

  const splitField = (combined: number): [number, number] => {
    const value = Number.isFinite(combined) ? combined : 0
    const outbound = roundCents(value / 2)
    return [outbound, roundCents(value - outbound)]
  }

  const [outboundSubtotal, returnSubtotal] = splitField(pricing.subtotal)
  const [outboundGratuity, returnGratuity] = splitField(pricing.gratuityAmount)
  const [outboundTaxes, returnTaxes] = splitField(pricing.taxes)

  // P = (2P - D + D) / 2. The return leg takes the remainder so the pair always
  // adds back up to the combined total.
  const outboundTotal = roundCents((pricing.total + discountAmount) / 2)
  const returnTotal = roundCents(pricing.total - outboundTotal)

  return {
    outbound: {
      subtotal: outboundSubtotal,
      gratuityAmount: outboundGratuity,
      taxes: outboundTaxes,
      total: outboundTotal,
      breakdown: oneWayBreakdown.map((line) => ({ ...line })),
    },
    return: {
      subtotal: returnSubtotal,
      gratuityAmount: returnGratuity,
      taxes: returnTaxes,
      total: returnTotal,
      // The return-trip discount rides on the return leg, exactly as
      // `applyRoundTripPricing` places it.
      breakdown: [
        ...oneWayBreakdown.map((line) => ({ ...line })),
        ...returnDiscountLines.map((line) => ({ ...line })),
      ],
    },
  }
}

/**
 * Push a discount taken off the COMBINED total down into both legs.
 *
 * The discount is split pro-rata by leg total and added to each leg as its own
 * negative line, so each row reads honestly on its own and the pair still sums
 * to the charged amount. Legs whose share rounds to zero are left untouched
 * rather than carrying a `-$0.00` line.
 *
 * `subtotal`, `gratuityAmount` and `taxes` are deliberately unchanged: a
 * discount comes off the grand total, the same way checkout and the Builder's
 * Apply Discount dialog treat it.
 */
/** The booking columns a per-leg payment credit is computed from. */
export interface PaidRowShare {
  total_amount?: number | null
  deposit_amount?: number | null
}

/**
 * The amount to credit ONE booking row for a charge covering `rowCount` rows.
 *
 * A round trip is charged once but stored as two rows, so every writer that
 * records "what was paid" has to credit each row its own share. Writing the
 * combined charge onto both rows double-counts revenue — a $475.74 trip reports
 * $951.48 paid — and leaves each leg showing a bogus balance against its own
 * smaller `total_amount`. Two writers do this and must agree: the Stripe webhook
 * (the normal path) and `/api/booking/confirmation` (the fallback the
 * confirmation page hits for BOTH legs, and the only one that runs where no
 * webhook is registered).
 *
 * - Single row (every one-way booking): the full charge, unchanged.
 * - Paired round trip: the row's own `total_amount` — or `deposit_amount` when
 *   the charge was a deposit. Those per-leg figures are authoritative rather
 *   than a fresh pro-rata split, because `create-payment-intent` already wrote
 *   them so the pair sums exactly to what Stripe captured.
 *
 * A missing or non-positive per-leg figure falls back to an even split, never to
 * the full charge: a legacy row with null pricing should under-report its share
 * rather than inflate the pair's revenue.
 */
export function resolveRowPaidAmount(params: {
  row: PaidRowShare
  rowCount: number
  chargedAmount: number
  isDeposit: boolean
}): number {
  const { row, rowCount, chargedAmount, isDeposit } = params

  if (rowCount <= 1) return chargedAmount

  const share = Number(isDeposit ? row.deposit_amount : row.total_amount)
  if (Number.isFinite(share) && share > 0) return roundCents(share)

  return roundCents(chargedAmount / rowCount)
}

export function splitDiscountAcrossLegs(
  allocation: LegAllocation,
  discountAmount: number,
  label: string,
  metadata?: Record<string, unknown>
): LegAllocation {
  const target = roundCents(discountAmount)
  if (!Number.isFinite(target) || target <= 0) return allocation

  const [outboundShare, returnShare] = allocateAmountByShare(target, [
    allocation.outbound.total,
    allocation.return.total,
  ])

  const withShare = (leg: LegPricing, share: number): LegPricing => {
    if (share <= 0) return leg
    return {
      ...leg,
      total: roundCents(leg.total - share),
      breakdown: [
        ...leg.breakdown,
        {
          label,
          amount: -share,
          type: 'discount' as const,
          ...(metadata ? { metadata } : {}),
        },
      ],
    }
  }

  return {
    outbound: withShare(allocation.outbound, outboundShare),
    return: withShare(allocation.return, returnShare),
  }
}

/** Everything `create-payment-intent` knows about a round trip's split. */
export interface ResolveLegAllocationInput {
  /** The booking has a return date, so it books as two rows. */
  isRoundTrip: boolean
  /** The charge is an operator's hand-set quote (Get Quote flow). */
  operatorPriced: boolean
  /** The split `calculate-price` produced, carried in memory through the flow. */
  carriedAllocation?: LegAllocation | null
  /** The split persisted on the quote at vehicle selection. */
  persistedAllocation?: LegAllocation | null
  /** The combined price actually being charged. */
  pricing: CombinedRoundTripPricing
}

/**
 * Decide how a round trip is split into its two booking rows.
 *
 * Calculator-priced trips use the split the calculator produced — carried by
 * the client, or persisted on the quote for a resumed link — and only fall back
 * to deriving one for legacy quotes that carried neither.
 *
 * Operator-priced trips ALWAYS derive from the operator's number and ignore any
 * carried or persisted split. Those come from the calculator, and the session's
 * quote token is reused across trips: a customer who selected a priced vehicle
 * and then hit Get Quote on another would otherwise have the calculator's legs
 * laid over a price the operator set by hand, so the rows would not sum to the
 * charge. The operator quoted the whole trip as one figure, so the legs are the
 * even halves of it — the same way a mirrored calculator round trip splits.
 *
 * `null` means "do not pair": either it is not a round trip, or there is no
 * positive total to split.
 */
export function resolveRoundTripLegAllocation(
  input: ResolveLegAllocationInput
): LegAllocation | null {
  if (!input.isRoundTrip) return null
  if (input.operatorPriced) return deriveLegAllocation(input.pricing)
  return (
    input.carriedAllocation ??
    input.persistedAllocation ??
    deriveLegAllocation(input.pricing)
  )
}
